// Note: affiliateApi import removed - this utility can be used independently
// The tracking functionality will be skipped if the API is not available

/**
 * Extracts referral code from URL parameters
 */
export const getReferralCodeFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('ref');
};

/**
 * Stores referral code with a 7-day lifetime for later use during registration.
 * Uses both a cookie and localStorage as a fallback.
 */
export const storeReferralCode = (referralCode: string): void => {
  if (typeof window === 'undefined') return;

  const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
  const expirationTime = Date.now() + sevenDaysInMs;

  // Cookie (primary, 7 days)
  const expiresDate = new Date(expirationTime);
  document.cookie = `affiliate_referral=${encodeURIComponent(
    referralCode
  )}; expires=${expiresDate.toUTCString()}; path=/; SameSite=Lax`;

  // LocalStorage fallback with the same 7-day expiry
  localStorage.setItem('referralCode', referralCode);
  localStorage.setItem('referralCodeExpiration', expirationTime.toString());
};

/**
 * Reads a value from cookies by name.
 */
const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie ? document.cookie.split(';') : [];
  const prefix = `${name}=`;

  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.substring(prefix.length));
    }
  }

  return null;
};

/**
 * Gets stored referral code (prefers cookie, falls back to localStorage)
 */
export const getStoredReferralCode = (): string | null => {
  if (typeof window === 'undefined') return null;

  // Prefer cookie if present
  const cookieCode = getCookieValue('affiliate_referral');
  if (cookieCode) {
    return cookieCode;
  }

  const referralCode = localStorage.getItem('referralCode');
  const expirationTime = localStorage.getItem('referralCodeExpiration');

  if (!referralCode || !expirationTime) {
    return null;
  }

  // Check if expired (7-day window)
  if (Date.now() > parseInt(expirationTime, 10)) {
    localStorage.removeItem('referralCode');
    localStorage.removeItem('referralCodeExpiration');
    return null;
  }

  return referralCode;
};

/**
 * Clears stored referral code from cookie and localStorage
 */
export const clearStoredReferralCode = (): void => {
  if (typeof window === 'undefined') return;

  // Clear cookie
  document.cookie = 'affiliate_referral=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';

  // Clear localStorage
  localStorage.removeItem('referralCode');
  localStorage.removeItem('referralCodeExpiration');
};

/**
 * Tracks affiliate click and stores referral code
 */
export const trackAffiliateClick = async (referralCode: string): Promise<void> => {
  try {
    // Import affiliateApi dynamically to avoid circular dependencies
    const { affiliateApi } = await import('../api/affiliate/affiliate.api');

    // Track the click on the backend
    await affiliateApi.trackClick(referralCode);

    // Store referral code for later use
    storeReferralCode(referralCode);
  } catch (error) {
    console.error('Failed to track affiliate click:', error);
    // Still store the referral code even if tracking fails
    storeReferralCode(referralCode);
  }
};

/**
 * Initializes affiliate tracking on page load
 * Should be called in the main app component or router
 */
export const initializeAffiliateTracking = async (): Promise<void> => {
  const referralCode = getReferralCodeFromUrl();
  const { extractUtmParameters, storeUtmParameters } = await import('./utm-utils');
  const utmParams = extractUtmParameters();

  if (referralCode) {
    await trackAffiliateClick(referralCode);
    // When link only has ref (no UTM), store affiliate UTM so user_acquisitions is still filled on signup
    const hasUtmInUrl =
      utmParams.utm_source ||
      utmParams.utm_medium ||
      utmParams.utm_campaign ||
      utmParams.utm_term ||
      utmParams.utm_content;
    if (!hasUtmInUrl) {
      storeUtmParameters({
        utm_source: 'affiliate',
        utm_medium: 'referral',
      });
    }
  }

  // Store UTM parameters from URL for user acquisition tracking (when not already set by ref above)
  if (
    utmParams.utm_source ||
    utmParams.utm_medium ||
    utmParams.utm_campaign ||
    utmParams.utm_term ||
    utmParams.utm_content
  ) {
    storeUtmParameters(utmParams);
  }
};

/**
 * Generates a shareable referral link
 * @param referralLink - The referral link to include in the text
 * @param t - Translation function from useTranslationByKey
 */
export const generateShareableText = (referralLink: string, t: (key: string, options?: any) => string): string => {
  return t('AFFILIATE.SHAREABLE_TEXT', { referralLink });
};

/**
 * Formats commission amount for display
 * @param amount - The commission amount to format
 * @param t - Translation function from useTranslationByKey
 */
export const formatCommission = (amount: number, t: (key: string, options?: any) => string): string => {
  return t('AFFILIATE.COMMISSION_FORMAT', { amount: amount.toLocaleString() });
};

/**
 * Gets commission rate display text
 * @param rate - The commission rate percentage
 * @param t - Translation function from useTranslationByKey
 */
export const getCommissionRateText = (rate: number, t: (key: string, options?: any) => string): string => {
  return t('AFFILIATE.COMMISSION_RATE_TEXT', { rate });
};
